import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Puck, usePuck } from '@puckeditor/core';
import type { Data } from '@puckeditor/core';
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  Copy,
  Eye,
  EyeOff,
  LayoutList,
  Maximize2,
  Minimize2,
  Monitor,
  MoreHorizontal,
  MoveVertical,
  PanelLeft,
  PanelRight,
  Plus,
  Redo2,

  Save,
  Search,
  Smartphone,
  Tablet,
  Trash2,
  Undo2,
  WandSparkles,
} from 'lucide-react';
import { useNavigate, useParams, type NavigateFunction } from 'react-router-dom';
import { api } from '../../lib/api';
import type { Page } from '../../lib/types';
import { puckConfig } from './puck-config';
import { editorBlockMeta } from './editor-meta';
import { setEditorLocale } from './editor-locale';
import Modal from '../../components/ui/Modal';
import PublishButton from '../../components/ui/PublishButton';
import { MediaPicker } from '../../components/ui/MediaPicker';
import { CollapsibleSection } from '../../components/ui/CollapsibleSection';
import { Checkbox, Field, Input, Select, Textarea } from '../../components/ui/Field';

const ROOT_ZONE = 'root:default-zone';
const LS_LEFT = 'editor.leftW';
const LS_RIGHT = 'editor.rightW';
const PANEL_RAIL_WIDTH = 72;
const PANEL_LEFT_DEFAULT = 280;
const PANEL_RIGHT_DEFAULT = 340;
const PANEL_WIDE_BREAKPOINT = 1680;

const LOCALE_STYLE: Record<string, { bg: string; color: string; label: string }> = {
  es: { bg: '#312e81', color: '#c7d2fe', label: 'ES' },
  en: { bg: '#064e3b', color: '#6ee7b7', label: 'EN' },
};

const VIEWPORT_CONFIG = {
  desktop: { refWidth: 1440, label: 'Escritorio' },
  tablet:  { refWidth: 768,  label: 'Tablet'     },
  mobile:  { refWidth: 390,  label: 'Móvil'      },
} as const;
type Viewport = keyof typeof VIEWPORT_CONFIG;

function vpScale(refWidth: number, containerW: number): number {
  return containerW > 0 && containerW < refWidth ? containerW / refWidth : 1;
}
function vpZoomPct(refWidth: number, containerW: number): number {
  return Math.round(vpScale(refWidth, containerW) * 100);
}
function vpDisplayWidth(refWidth: number, containerW: number): number {
  return containerW > 0 ? Math.min(containerW, refWidth) : refWidth;
}

type BlockRecord = { type: string; props: Record<string, unknown> };
type SaveState = 'pristine' | 'saved' | 'saving' | 'error';
type SidePanelMode = 'open' | 'collapsed';

type PuckFieldDef = {
  type?: string;
  label?: string;
  isAiText?: boolean;
  options?: Array<{ value: string; label: string }>;
  min?: number;
  max?: number;
  render?: (args: { value: any; onChange: (value: any) => void }) => React.ReactNode;
  arrayFields?: Record<string, PuckFieldDef>;
  objectFields?: Record<string, PuckFieldDef>;
};

type BlockSection = { key: string; title: string; fields: string[]; defaultOpen?: boolean };

function clampWidth(w: number) {
  return Math.max(240, Math.min(440, w));
}

function loadWidth(key: string, fallback: number) {
  const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
  const n = raw ? parseInt(raw, 10) : NaN;
  return Number.isFinite(n) ? clampWidth(n) : fallback;
}

function preferredPanelMode(): SidePanelMode {
  return 'open';
}

function createId(type: string, index: number) {
  return `${type}-${index}-${Math.random().toString(36).slice(2, 11)}`;
}

function ensureDataIds(data: Data): Data {
  return {
    ...data,
    content: data.content.map((item, index) => ({
      ...item,
      props: {
        ...item.props,
        id: (item.props as { id?: string }).id || createId(item.type, index),
      },
    })),
  };
}

function cloneData(data: Data): Data {
  return JSON.parse(JSON.stringify(data)) as Data;
}

function parseContentJson(contentJson: string): Data {
  let blocks: BlockRecord[] = [];
  try {
    blocks = JSON.parse(contentJson) as BlockRecord[];
  } catch {
    blocks = [];
  }
  return ensureDataIds({
    content: blocks.map((block, index) => ({
      type: block.type,
      props: {
        ...block.props,
        id: (block.props as { id?: string }).id || createId(block.type, index),
      },
    })),
    root: {},
    zones: {},
  });
}

function serializeData(data: Data) {
  const blocks = data.content.map((item) => ({ type: item.type, props: item.props }));
  return JSON.stringify(blocks);
}

function blockLabel(type: string) {
  return (puckConfig.components[type] as { label?: string } | undefined)?.label ?? type;
}

function buildInitialProps(type: string) {
  const component = puckConfig.components[type] as { defaultProps?: Record<string, unknown> } | undefined;
  return { ...(component?.defaultProps ?? {}) };
}

function fmtSavedAt(date: Date) {
  return date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
}

function defaultToolbarMode() {
  if (typeof window !== 'undefined' && window.innerWidth < 1700) return 'compact';
  return 'full';
}

function AddBlockMenu({
  onAdd,
  compact = false,
}: {
  onAdd: (type: string) => void;
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const entries = Object.keys(puckConfig.components)
    .map((type) => {
      const meta = editorBlockMeta[type];
      return {
        type,
        label: blockLabel(type),
        description: meta?.description ?? '',
        icon: meta?.icon ?? LayoutList,
      };
    })
    .filter((entry) => {
      const q = query.trim().toLowerCase();
      if (!q) return true;
      return entry.label.toLowerCase().includes(q) || entry.description.toLowerCase().includes(q);
    });

  function close() {
    setOpen(false);
    setQuery('');
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center justify-center gap-2 rounded-md border border-ui-border bg-ui-surface-2 px-3 py-2 text-sm text-ui-text hover:border-ui-border-strong hover:text-ui-text ${compact ? 'w-10 px-0' : 'w-full'}`}
      >
        <Plus size={16} />
        {!compact && 'Añadir bloque'}
      </button>
      {open && (
        <Modal title="¿Qué bloque quieres añadir?" onClose={close} wide>
          <div className="space-y-4">
            <div className="relative">
              <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ui-text-subtle" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Busca un bloque..."
                className="w-full rounded-md border border-ui-border bg-ui-bg py-2.5 pl-9 pr-3 text-sm text-ui-text placeholder:text-ui-text-subtle focus:outline-none focus:border-ui-accent"
              />
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {entries.map((entry, i) => {
                const Icon = entry.icon;
                return (
                  <motion.button
                    key={entry.type}
                    type="button"
                    onClick={() => {
                      onAdd(entry.type);
                      close();
                    }}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(0.03 * i, 0.3) }}
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    className="rounded-xl border border-ui-border bg-ui-bg p-4 text-left transition-colors hover:border-ui-border-strong"
                  >
                    <div className="flex items-start gap-3">
                      <div className="rounded-lg bg-ui-surface p-2 text-ui-accent">
                        <Icon size={18} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-ui-text">{entry.label}</p>
                        {entry.description && (
                          <p className="mt-1 text-sm text-ui-text-subtle">{entry.description}</p>
                        )}
                      </div>
                    </div>
                  </motion.button>
                );
              })}
            </div>

            {entries.length === 0 && (
              <p className="py-8 text-center text-sm text-ui-text-subtle">No hay bloques que coincidan con la búsqueda.</p>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}

function Divider({ onResize, onResizeStart, onResizeEnd }: {
  onResize: (dx: number) => void;
  onResizeStart?: () => void;
  onResizeEnd?: () => void;
}) {
  const last = useRef(0);
  function onPointerDown(e: React.PointerEvent) {
    last.current = e.clientX;
    onResizeStart?.();
    function move(ev: PointerEvent) {
      onResize(ev.clientX - last.current);
      last.current = ev.clientX;
    }
    function up() {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      onResizeEnd?.();
    }
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }
  return <div onPointerDown={onPointerDown} style={{ width: 5, cursor: 'col-resize', background: 'rgb(var(--ui-border-ch))', flexShrink: 0 }} />;
}

function PanelIconButton({
  title,
  icon,
  onClick,
}: {
  title: string;
  icon: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-ui-border bg-ui-surface-2 text-ui-text-muted transition-colors hover:border-ui-border-strong hover:text-ui-text"
    >
      {icon}
    </button>
  );
}

function CollapsedPanelRail({
  side,
  title,
  meta,
  icon,
  accent,
  onExpand,
  children,
}: {
  side: 'left' | 'right';
  title: string;
  meta: string;
  icon: React.ReactNode;
  accent?: React.ReactNode;
  onExpand: () => void;
  children?: React.ReactNode;
}) {
  return (
    <div
      style={{
        width: PANEL_RAIL_WIDTH,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
        padding: 5,
        borderLeft: side === 'right' ? '1px solid rgb(var(--ui-border-ch))' : undefined,
        borderRight: side === 'left' ? '1px solid rgb(var(--ui-border-ch))' : undefined,
        background: 'rgb(var(--ui-surface-ch))',
      }}
    >
      <button
        type="button"
        onClick={onExpand}
        title={`Expandir panel de ${title.toLowerCase()}`}
        aria-label={`Expandir panel de ${title.toLowerCase()}`}
        className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-ui-border bg-ui-surface-2 text-ui-accent transition-colors hover:border-ui-border-strong"
      >
        {icon}
      </button>
      <div className="flex min-h-0 flex-1 flex-col items-center gap-3">
        <div className="rounded-2xl bg-ui-bg px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-ui-text-subtle">
          {meta}
        </div>
        {accent}
        {children}
      </div>
      <div className="[writing-mode:vertical-rl] rotate-180 text-[10px] font-semibold uppercase tracking-[0.22em] text-ui-text-subtle">
        {title}
      </div>
    </div>
  );
}

function AutoGrowTextarea({
  value,
  onChange,
  rows = 1,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  placeholder?: string;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);

  return (
    <Textarea
      ref={ref}
      value={value}
      rows={rows}
      placeholder={placeholder}
      className="min-h-[44px] resize-none overflow-hidden"
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function ToolbarMenu({
  label,
  icon,
  children,
}: {
  label: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`inline-flex h-10 items-center gap-1.5 rounded-full border px-3 text-sm transition-colors ${
          open
            ? 'border-ui-accent bg-ui-bg text-ui-text'
            : 'border-ui-border bg-ui-surface-2 text-ui-text-muted hover:border-ui-border-strong hover:text-ui-text'
        }`}
      >
        {icon}
        <span>{label}</span>
        <ChevronDown size={13} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-[calc(100%+0.5rem)] z-40 min-w-[220px] rounded-2xl border border-ui-border bg-ui-surface p-2 shadow-2xl">
          {children}
        </div>
      )}
    </div>
  );
}

function ToolbarAction({
  label,
  hint,
  tone = 'default',
  onClick,
}: {
  label: string;
  hint: string;
  tone?: 'default' | 'danger';
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full rounded-xl px-3 py-2 text-left transition-colors ${
        tone === 'danger'
          ? 'hover:bg-red-500/10'
          : 'hover:bg-ui-bg'
      }`}
    >
      <div className={tone === 'danger' ? 'text-sm font-medium text-red-400' : 'text-sm font-medium text-ui-text'}>{label}</div>
      <div className="mt-0.5 text-xs text-ui-text-subtle">{hint}</div>
    </button>
  );
}

function LanguagePopover({
  currentLabel,
  siblingLabel,
  onOpenSibling,
  onCopyBlocks,
}: {
  currentLabel: string;
  siblingLabel: string;
  onOpenSibling: () => void;
  onCopyBlocks: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-sm transition-colors ${
          open
            ? 'border-ui-accent bg-ui-surface-2 text-ui-text'
            : 'border-ui-border bg-ui-surface text-ui-text-muted hover:border-ui-border-strong hover:text-ui-text'
        }`}
      >
        <span className="rounded-md bg-ui-bg px-2 py-0.5 text-xs font-semibold text-ui-text">{currentLabel}</span>
        <span className="text-ui-text-subtle">↔</span>
        <span className="rounded-md bg-ui-bg px-2 py-0.5 text-xs font-semibold text-ui-text">{siblingLabel}</span>
        <ChevronDown size={13} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-[calc(100%+0.5rem)] z-40 min-w-[260px] rounded-2xl border border-ui-border bg-ui-surface p-2 shadow-2xl">
          <div className="px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-ui-text-subtle">Idiomas vinculados</div>
          <ToolbarAction
            label={`Abrir versión ${siblingLabel}`}
            hint={`Cambia al editor de la versión ${siblingLabel} de esta misma página.`}
            onClick={() => {
              setOpen(false);
              onOpenSibling();
            }}
          />
          <ToolbarAction
            label={`Copiar bloques desde ${siblingLabel}`}
            hint={`Reemplaza los bloques actuales con los de la versión ${siblingLabel}.`}
            onClick={() => {
              setOpen(false);
              onCopyBlocks();
            }}
          />
        </div>
      )}
    </div>
  );
}

// Botón "Ayúdame" a nivel de sección: reescribe de una vez todos los textos
// visibles del bloque con un mismo tono. Muestra antes qué textos va a tocar
// y deja las imágenes y los enlaces intactos.
function SectionAiButton({
  blockType,
  fields,
  props,
  locale,
  onApply,
}: {
  blockType: string;
  fields: { name: string; label: string }[];
  props: Record<string, unknown>;
  locale: string;
  onApply: (patch: Record<string, unknown>) => void;
}) {
  const [open, setOpen] = useState(false);
  const [tone, setTone] = useState('');
  const [instruction, setInstruction] = useState('');
  const [showInstruction, setShowInstruction] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');

  // Textos de la sección con su contenido actual (para previsualizar qué se reescribe).
  const items = fields.map((f) => ({
    ...f,
    current: typeof props[f.name] === 'string' ? (props[f.name] as string).trim() : '',
  }));

  function start() {
    setTone('');
    setInstruction('');
    setShowInstruction(false);
    setError('');
    setOpen(true);
  }
  function close() {
    setOpen(false);
    setRunning(false);
  }

  async function run() {
    if (running) return;
    setRunning(true);
    setError('');
    const toneInstruction = AI_TONES.find((t) => t.key === tone)?.instruction ?? '';
    const combined = [toneInstruction, instruction.trim()].filter(Boolean).join(' ');
    try {
      const patch: Record<string, unknown> = {};
      for (const f of fields) {
        const raw = props[f.name];
        const value = typeof raw === 'string' ? raw.trim() : '';
        const { value: generated } = await api.ai.block(blockType, f.name, {
          fieldLabel: f.label,
          locale,
          mode: value ? 'rewrite' : 'generate',
          instruction: combined || undefined,
          context: value || undefined,
          currentProps: props,
        });
        patch[f.name] = generated;
      }
      if (Object.keys(patch).length > 0) onApply(patch);
      close();
    } catch (err) {
      setError((err as Error).message);
      setRunning(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={start}
        className="inline-flex items-center gap-1 rounded-md border border-ui-border px-2 py-1 text-sm text-ui-text-muted hover:border-ui-border-strong hover:text-ui-text"
      >
        <WandSparkles size={13} />
        Mejorar toda la sección
      </button>
      {open && (
        <Modal title="Mejorar toda la sección" onClose={close}>
          <div className="space-y-4">
            <p className="text-sm text-ui-text-muted">
              La IA reescribirá a la vez estos textos de la sección. Tú eliges el tono; las imágenes y los botones no se tocan.
            </p>

            <div className="space-y-1.5 rounded-md border border-ui-border bg-ui-bg px-3 py-2.5">
              <p className="text-sm text-ui-text-subtle">Se reescribirán:</p>
              <ul className="space-y-1">
                {items.map((it) => (
                  <li key={it.name} className="flex items-baseline gap-2 text-sm">
                    <span className="shrink-0 text-ui-text">{it.label}</span>
                    <span className="truncate text-ui-text-subtle">
                      {it.current ? `· ${it.current}` : '· (vacío, lo redactaremos)'}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-2">
              <p className="text-sm text-ui-text-subtle">Tono (opcional)</p>
              <div className="flex flex-wrap gap-2">
                {AI_TONES.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTone((prev) => (prev === t.key ? '' : t.key))}
                    className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
                      tone === t.key
                        ? 'border-ui-accent bg-ui-accent/10 text-ui-text'
                        : 'border-ui-border bg-ui-bg text-ui-text-muted hover:border-ui-border-strong'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {showInstruction ? (
              <Textarea
                value={instruction}
                onChange={(e) => setInstruction(e.target.value)}
                rows={2}
                autoFocus
                placeholder="Ej. Enfócalo en familias que buscan su primera casa."
                className="resize-none"
              />
            ) : (
              <button
                type="button"
                onClick={() => setShowInstruction(true)}
                className="text-sm text-ui-text-muted hover:text-ui-text"
              >
                Pedir algo concreto…
              </button>
            )}

            {error && <p className="text-sm text-ui-danger">{error}</p>}

            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={close}
                className="flex-1 rounded-md border border-ui-border px-4 py-2 text-sm text-ui-text-muted hover:text-ui-text"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => void run()}
                disabled={running}
                className="flex-1 rounded-md bg-ui-accent px-4 py-2 text-sm font-medium text-ui-text-inverse disabled:opacity-50"
              >
                {running ? 'Reescribiendo…' : 'Reescribir textos'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// Acciones rápidas de reescritura — instrucciones predefinidas, un clic.
const AI_ACTIONS: { key: string; label: string; instruction: string }[] = [
  { key: 'short', label: 'Más corto', instruction: 'Hazlo más corto, conserva el mensaje principal.' },
  { key: 'clear', label: 'Más claro', instruction: 'Reescríbelo para que se entienda mejor, con lenguaje sencillo y directo.' },
  { key: 'persuasive', label: 'Más persuasivo', instruction: 'Hazlo más persuasivo y atractivo, para invitar a dar el siguiente paso.' },
  { key: 'fix', label: 'Corregir', instruction: 'Corrige la ortografía y la gramática sin cambiar el sentido ni el estilo.' },
];

const AI_TONES: { key: string; label: string; instruction: string }[] = [
  { key: 'cercano', label: 'Cercano', instruction: 'Usa un tono cercano y amigable.' },
  { key: 'profesional', label: 'Profesional', instruction: 'Usa un tono profesional y serio.' },
  { key: 'premium', label: 'Premium', instruction: 'Usa un tono elegante y premium.' },
  { key: 'directo', label: 'Directo', instruction: 'Usa un tono directo y conciso.' },
];

function FieldAiButton({
  blockType,
  fieldName,
  fieldLabel,
  value,
  props,
  locale,
  onApply,
  onApplied,
}: {
  blockType: string;
  fieldName: string;
  fieldLabel: string;
  value: string;
  props: Record<string, unknown>;
  locale: string;
  onApply: (next: string) => void;
  onApplied?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [action, setAction] = useState('');
  const [instruction, setInstruction] = useState('');
  const [showInstruction, setShowInstruction] = useState(false);
  const [tone, setTone] = useState('');
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');

  const hasText = value.trim().length > 0;

  function start() {
    setText(value);
    setAction('');
    setInstruction('');
    setShowInstruction(false);
    setTone('');
    setError('');
    setOpen(true);
  }

  function close() {
    setOpen(false);
    setRunning(false);
  }

  // Combina la acción elegida + el tono + lo que el usuario pida a mano,
  // tomando como base el texto (editable) que el usuario tiene a la vista.
  async function run() {
    if (running) return;
    const base = text.trim();
    setRunning(true);
    setError('');
    const actionInstruction = AI_ACTIONS.find((a) => a.key === action)?.instruction ?? '';
    const toneInstruction = AI_TONES.find((t) => t.key === tone)?.instruction ?? '';
    const combined = [actionInstruction, toneInstruction, instruction.trim()].filter(Boolean).join(' ');
    try {
      const { value: generated } = await api.ai.block(blockType, fieldName, {
        fieldLabel,
        locale,
        mode: base ? 'rewrite' : 'generate',
        instruction: combined || undefined,
        context: base || undefined,
        currentProps: props,
      });
      onApply(generated);
      onApplied?.();
      close();
    } catch (err) {
      setError((err as Error).message);
      setRunning(false);
    }
  }

  return (
    <>
      <div className="mt-2 flex justify-end">
        <button
          type="button"
          onClick={start}
          className="inline-flex items-center gap-1 rounded-md border border-ui-border px-2 py-1 text-sm text-ui-text-muted hover:border-ui-border-strong hover:text-ui-text"
        >
          <WandSparkles size={13} />
          {hasText ? 'Mejorar con IA' : 'Escribir con IA'}
        </button>
      </div>
      {open && (
        <Modal title={`${hasText ? 'Mejorar' : 'Escribir'} «${fieldLabel}»`} onClose={close}>
          <div className="space-y-4">
            <p className="text-sm text-ui-text-muted">
              {hasText
                ? 'Ajusta el texto si quieres, elige cómo mejorarlo y pulsa Generar.'
                : 'Elige el tono si quieres y pulsa Escribir con IA para que la IA redacte este texto.'}
            </p>

            {hasText && (
              <div className="space-y-1">
                <p className="text-sm text-ui-text-subtle">Texto actual</p>
                <Textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={3}
                  className="resize-none"
                />
              </div>
            )}

            {hasText && (
              <div className="space-y-2">
                <p className="text-sm text-ui-text-subtle">Cómo mejorarlo (opcional)</p>
                <div className="flex flex-wrap gap-2">
                  {AI_ACTIONS.map((a) => (
                    <button
                      key={a.key}
                      type="button"
                      onClick={() => setAction((prev) => (prev === a.key ? '' : a.key))}
                      className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
                        action === a.key
                          ? 'border-ui-accent bg-ui-accent/10 text-ui-text'
                          : 'border-ui-border bg-ui-bg text-ui-text-muted hover:border-ui-border-strong'
                      }`}
                    >
                      {a.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <p className="text-sm text-ui-text-subtle">Tono (opcional)</p>
              <div className="flex flex-wrap gap-2">
                {AI_TONES.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTone((prev) => (prev === t.key ? '' : t.key))}
                    className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
                      tone === t.key
                        ? 'border-ui-accent bg-ui-accent/10 text-ui-text'
                        : 'border-ui-border bg-ui-bg text-ui-text-muted hover:border-ui-border-strong'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {showInstruction ? (
              <Textarea
                value={instruction}
                onChange={(e) => setInstruction(e.target.value)}
                rows={2}
                autoFocus
                placeholder="Pedir algo concreto…"
                className="resize-none"
              />
            ) : (
              <button
                type="button"
                onClick={() => setShowInstruction(true)}
                className="text-sm text-ui-text-muted hover:text-ui-text"
              >
                Pedir algo concreto…
              </button>
            )}

            {error && <p className="text-sm text-ui-danger">{error}</p>}

            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={close}
                className="flex-1 rounded-md border border-ui-border px-4 py-2 text-sm text-ui-text-muted hover:text-ui-text"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => void run()}
                disabled={running}
                className="flex-1 rounded-md bg-ui-accent px-4 py-2 text-sm font-medium text-ui-text-inverse disabled:opacity-50"
              >
                {running ? 'Generando…' : hasText ? 'Generar' : 'Escribir con IA'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// Campo de texto con el botón de IA. Cuando la IA escribe en el campo,
// un halo de gradiente animado lo recorre brevemente (estilo iOS 26).
function AiTextField({
  blockType,
  fieldName,
  fieldLabel,
  value,
  onChange,
  props,
  locale,
  rows,
  placeholder,
}: {
  blockType: string;
  fieldName: string;
  fieldLabel: string;
  value: string;
  onChange: (value: string) => void;
  props: Record<string, unknown>;
  locale: string;
  rows?: number;
  placeholder?: string;
}) {
  const [glow, setGlow] = useState(false);

  function flash() {
    setGlow(true);
    window.setTimeout(() => setGlow(false), 1600);
  }

  return (
    <>
      <div className="relative rounded-md">
        <AutoGrowTextarea value={value} onChange={onChange} rows={rows} placeholder={placeholder} />
        <AnimatePresence>
          {glow && (
            <motion.div
              key="glow"
              aria-hidden
              className="pointer-events-none absolute inset-0 rounded-md"
              style={{
                padding: '2px',
                WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
                WebkitMaskComposite: 'xor',
                maskComposite: 'exclude',
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35, ease: 'easeOut' }}
            >
              <motion.div
                className="absolute inset-[-50%]"
                style={{
                  background:
                    'conic-gradient(from 0deg, #3b82f6, #a855f7, #ec4899, #f59e0b, #22d3ee, #3b82f6)',
                }}
                animate={{ rotate: 360 }}
                transition={{ duration: 1.4, ease: 'linear', repeat: Infinity }}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <FieldAiButton
        blockType={blockType}
        fieldName={fieldName}
        fieldLabel={fieldLabel}
        value={value}
        props={props}
        locale={locale}
        onApply={onChange}
        onApplied={flash}
      />
    </>
  );
}

function FieldControl({
  blockType,
  fieldName,
  fieldDef,
  value,
  onChange,
  props,
  locale,
}: {
  blockType: string;
  fieldName: string;
  fieldDef: PuckFieldDef;
  value: unknown;
  onChange: (value: unknown) => void;
  props: Record<string, unknown>;
  locale: string;
}) {
  const fieldType = fieldDef.type ?? 'text';

  if (fieldType === 'custom' && fieldDef.render) {
    return <>{fieldDef.render({ value, onChange })}</>;
  }

  if (fieldType === 'textarea') {
    const current = typeof value === 'string' ? value : '';
    return (
      <AiTextField
        blockType={blockType}
        fieldName={fieldName}
        fieldLabel={fieldDef.label ?? fieldName}
        value={current}
        onChange={onChange as (value: string) => void}
        props={props}
        locale={locale}
        rows={4}
      />
    );
  }

  if (fieldType === 'text') {
    const current = typeof value === 'string' ? value : '';
    return (
      <AiTextField
        blockType={blockType}
        fieldName={fieldName}
        fieldLabel={fieldDef.label ?? fieldName}
        value={current}
        onChange={onChange as (value: string) => void}
        props={props}
        locale={locale}
        placeholder={fieldDef.label}
      />
    );
  }

  if (fieldType === 'number') {
    return (
      <Input
        type="number"
        min={fieldDef.min}
        max={fieldDef.max}
        value={value == null ? '' : String(value)}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
      />
    );
  }

  if (fieldType === 'select' || fieldType === 'radio') {
    return (
      <Select value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {(fieldDef.options ?? []).map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
    );
  }

  if (fieldType === 'object' && fieldDef.objectFields) {
    const objectValue = (value as Record<string, unknown> | undefined) ?? {};
    return (
      <div className="space-y-4 rounded-xl border border-ui-border bg-ui-bg p-4">
        {Object.entries(fieldDef.objectFields).map(([nestedKey, nestedDef]) => (
          <Field key={nestedKey} label={nestedDef.label ?? nestedKey}>
            <FieldControl
              blockType={blockType}
              fieldName={nestedKey}
              fieldDef={nestedDef}
              value={objectValue[nestedKey]}
              onChange={(next) => onChange({ ...objectValue, [nestedKey]: next })}
              props={props}
              locale={locale}
            />
          </Field>
        ))}
      </div>
    );
  }

  if (fieldType === 'array' && fieldDef.arrayFields) {
    const items = Array.isArray(value) ? (value as Array<Record<string, unknown>>) : [];
    const fieldEntries = Object.entries(fieldDef.arrayFields);
    return (
      <div className="space-y-3">
        {items.map((item, index) => (
          <div key={index} className="rounded-xl border border-ui-border bg-ui-bg p-4">
            <div className="mb-4 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wide text-ui-text-subtle">Item {index + 1}</span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (index === 0) return;
                    const next = [...items];
                    [next[index - 1], next[index]] = [next[index], next[index - 1]];
                    onChange(next);
                  }}
                  className="rounded-md border border-ui-border p-1 text-ui-text-subtle hover:text-ui-text"
                >
                  <ArrowUp size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (index === items.length - 1) return;
                    const next = [...items];
                    [next[index + 1], next[index]] = [next[index], next[index + 1]];
                    onChange(next);
                  }}
                  className="rounded-md border border-ui-border p-1 text-ui-text-subtle hover:text-ui-text"
                >
                  <ArrowDown size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => onChange(items.filter((_, itemIndex) => itemIndex !== index))}
                  className="rounded-md border border-ui-border p-1 text-red-400 hover:border-red-400/40"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            <div className="space-y-4">
              {fieldEntries.map(([nestedKey, nestedDef]) => (
                <Field key={nestedKey} label={nestedDef.label ?? nestedKey}>
                  <FieldControl
                    blockType={blockType}
                    fieldName={nestedKey}
                    fieldDef={nestedDef}
                    value={item?.[nestedKey]}
                    onChange={(next) => {
                      const nextItems = [...items];
                      nextItems[index] = { ...(nextItems[index] ?? {}), [nestedKey]: next };
                      onChange(nextItems);
                    }}
                    props={props}
                    locale={locale}
                  />
                </Field>
              ))}
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={() => {
            const nextItem = Object.fromEntries(
              fieldEntries.map(([nestedKey, nestedDef]) => {
                if (nestedDef.type === 'array') return [nestedKey, []];
                if (nestedDef.type === 'object') return [nestedKey, {}];
                return [nestedKey, ''];
              }),
            );
            onChange([...items, nextItem]);
          }}
          className="inline-flex items-center gap-2 rounded-md border border-ui-border bg-ui-surface px-3 py-2 text-sm text-ui-text-muted hover:text-ui-text"
        >
          <Plus size={14} />
          Añadir item
        </button>
      </div>
    );
  }

  return <Input value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value)} />;
}

function BlockInspector({
  block,
  selectedIndex,
  locale,
  onPropsPatch,
  isSyncing,
}: {
  block: { type: string; props: Record<string, unknown> } | null;
  selectedIndex: number;
  locale: string;
  onPropsPatch: (patch: Record<string, unknown>) => void;
  isSyncing?: boolean;
}) {
  if (!block) {
    return (
      <div className="flex h-full items-center justify-center px-8 text-center text-sm text-ui-text-subtle">
        Selecciona un bloque desde la lista o haciendo clic en la vista en vivo.
      </div>
    );
  }

  const component = puckConfig.components[block.type] as { fields?: Record<string, PuckFieldDef> } | undefined;
  const fields = component?.fields ?? {};
  const meta = editorBlockMeta[block.type];
  const Icon = meta?.icon ?? PanelRight;
  const usedFields = new Set(meta?.sections.flatMap((section) => section.fields) ?? []);
  const sections: BlockSection[] = meta?.sections
    ? [
        ...meta.sections,
        ...Object.keys(fields)
          .filter((fieldName) => !usedFields.has(fieldName))
          .map((fieldName) => ({ key: `advanced-${fieldName}`, title: 'Opciones avanzadas', fields: [fieldName] })),
      ]
    : [{ key: 'all', title: 'Contenido', defaultOpen: true, fields: Object.keys(fields) }];

  const groupedSections = sections.reduce<BlockSection[]>((acc, section) => {
    const last = acc[acc.length - 1];
    if (section.title === 'Opciones avanzadas' && last?.title === 'Opciones avanzadas') {
      last.fields.push(...section.fields);
      return acc;
    }
    acc.push({ ...section, fields: [...section.fields] });
    return acc;
  }, []);

  return (
    <div className="flex h-full flex-col" style={{ position: 'relative' }}>
      {isSyncing && <div className="sync-progress-bar" />}
      <div className="border-b border-ui-border px-5 py-4">
        <div className="mb-2 flex items-center gap-3">
          <div className="rounded-lg bg-ui-accent/15 p-2 text-ui-accent">
            <Icon size={18} />
          </div>
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-[0.18em] text-ui-text-subtle">Bloque {selectedIndex + 1}</p>
            <h2 className="truncate text-base font-semibold text-ui-text">{blockLabel(block.type)}</h2>
          </div>
        </div>
        <p className="text-sm text-ui-text-subtle">
          {editorBlockMeta[block.type]?.summary(block.props) ?? 'Configura el contenido y el diseño de este bloque.'}
        </p>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-4" data-syncing={isSyncing}>
        <div className="space-y-2">
          {groupedSections.map((section) => {
            const sectionFields = section.fields.filter((fieldName) => fields[fieldName]);
            if (sectionFields.length === 0) return null;
            const textFields = sectionFields.filter((fieldName) => {
              const def = fields[fieldName];
              const type = def?.type ?? 'text';
              return type === 'text' || type === 'textarea' || def?.isAiText === true;
            });
            return (
              <CollapsibleSection
                key={section.key}
                title={section.title}
                defaultOpen={section.defaultOpen ?? true}
                meta={`${sectionFields.length} ${sectionFields.length === 1 ? 'campo' : 'campos'}`}
              >
                {textFields.length > 0 && (
                  <div className="flex justify-end">
                    <SectionAiButton
                      blockType={block.type}
                      fields={textFields.map((fieldName) => ({
                        name: fieldName,
                        label: fields[fieldName]?.label ?? fieldName,
                      }))}
                      props={block.props}
                      locale={locale}
                      onApply={onPropsPatch}
                    />
                  </div>
                )}
                {sectionFields.map((fieldName) => {
                  const fieldDef = fields[fieldName];
                  return (
                    <Field key={fieldName} label={fieldDef.label ?? fieldName}>
                      <FieldControl
                        blockType={block.type}
                        fieldName={fieldName}
                        fieldDef={fieldDef}
                        value={block.props[fieldName]}
                        onChange={(next) => onPropsPatch({ [fieldName]: next })}
                        props={block.props}
                        locale={locale}
                      />
                    </Field>
                  );
                })}
              </CollapsibleSection>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Contextual popover anchored to a clicked element inside the preview iframe.
// Appears near the element with the focused field + its companions; disappears on outside click or Escape.
function ElementPopover({
  block,
  fieldName,
  top,
  left,
  onClose,
  onPropsPatch,
  locale,
  isSyncing,
}: {
  block: { type: string; props: Record<string, unknown> };
  fieldName: string;
  top: number;
  left: number;
  onClose: () => void;
  onPropsPatch: (patch: Record<string, unknown>) => void;
  locale: string;
  isSyncing?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const component = puckConfig.components[block.type] as { fields?: Record<string, PuckFieldDef> } | undefined;
  const allFields = component?.fields ?? {};
  const meta = editorBlockMeta[block.type];
  const companions = meta?.fieldCompanions?.[fieldName] ?? [];
  const fieldsToShow = [fieldName, ...companions].filter((f) => !!allFields[f]);

  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  if (fieldsToShow.length === 0) return null;
  const mainDef = allFields[fieldName]!;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: -6, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -6, scale: 0.97 }}
      transition={{ type: 'spring', stiffness: 500, damping: 36 }}
      style={{
        position: 'absolute',
        top,
        left,
        width: 272,
        zIndex: 40,
        borderRadius: 14,
        border: '1px solid rgb(var(--ui-border-ch))',
        background: 'rgb(var(--ui-surface-ch))',
        boxShadow: '0 10px 40px rgba(0,0,0,.30), 0 2px 8px rgba(0,0,0,.12)',
        overflow: 'hidden',
      }}
    >
      {isSyncing && <div className="sync-progress-bar" />}
      <div
        style={{
          padding: '7px 10px 7px 12px',
          borderBottom: '1px solid rgb(var(--ui-border-ch))',
          background: 'rgb(var(--ui-surface-2-ch))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 6,
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.14em',
            color: 'rgb(var(--ui-text-subtle-ch))',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {mainDef.label ?? fieldName}
        </span>
        <button
          type="button"
          onClick={onClose}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'rgb(var(--ui-text-subtle-ch))',
            padding: '2px 4px',
            borderRadius: 4,
            fontSize: 16,
            lineHeight: 1,
            flexShrink: 0,
          }}
          title="Cerrar"
        >
          ×
        </button>
      </div>
      <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 10 }} data-syncing={isSyncing}>
        {fieldsToShow.map((f, fi) => {
          const def = allFields[f]!;
          return (
            <div key={f}>
              {fi > 0 && (
                <label
                  style={{
                    display: 'block',
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: '0.06em',
                    color: 'rgb(var(--ui-text-subtle-ch))',
                    marginBottom: 4,
                  }}
                >
                  {def.label ?? f}
                </label>
              )}
              <FieldControl
                blockType={block.type}
                fieldName={f}
                fieldDef={def}
                value={block.props[f]}
                onChange={(next) => onPropsPatch({ [f]: next })}
                props={block.props}
                locale={locale}
              />
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}

// ── Part 2: Floating inspector popover ───────────────────────────────────────
// Shows primary fields of the selected block as an overlay on the preview area.
// Slides in from the right when a block is selected; disappears on deselect.
function FloatingInspector({
  block,
  selectedIndex,
  locale,
  onPropsPatch,
  rightIsOpen,
  onToggleRight,
  onDismiss,
  isSyncing,
}: {
  block: { type: string; props: Record<string, unknown> };
  selectedIndex: number;
  locale: string;
  onPropsPatch: (patch: Record<string, unknown>) => void;
  rightIsOpen: boolean;
  onToggleRight: () => void;
  onDismiss: () => void;
  isSyncing?: boolean;
}) {
  const meta = editorBlockMeta[block.type];
  const primaryFields = meta?.primaryFields ?? [];
  const component = puckConfig.components[block.type] as { fields?: Record<string, PuckFieldDef> } | undefined;
  const allFields = component?.fields ?? {};
  const renderable = primaryFields.filter((f) => !!allFields[f]);
  const Icon = meta?.icon ?? PanelRight;

  if (renderable.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20, scale: 0.97 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 20, scale: 0.97 }}
      transition={{ type: 'spring', stiffness: 420, damping: 34 }}
      style={{
        position: 'absolute',
        top: 16,
        right: 16,
        width: 288,
        zIndex: 30,
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 'calc(100% - 32px)',
        borderRadius: 16,
        border: '1px solid rgb(var(--ui-border-ch))',
        background: 'rgb(var(--ui-surface-ch))',
        boxShadow: '0 12px 48px rgba(0,0,0,.22), 0 2px 8px rgba(0,0,0,.12)',
        overflow: 'hidden',
      }}
    >
      {isSyncing && <div className="sync-progress-bar" />}
      <div
        style={{
          padding: '10px 12px',
          borderBottom: '1px solid rgb(var(--ui-border-ch))',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexShrink: 0,
        }}
      >
        <div className="rounded-lg bg-ui-accent/15 p-1.5 text-ui-accent">
          <Icon size={14} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-[0.16em] text-ui-text-subtle">Bloque {selectedIndex + 1}</p>
          <p className="truncate text-sm font-semibold text-ui-text">{blockLabel(block.type)}</p>
        </div>
        <button
          type="button"
          onClick={onToggleRight}
          className="flex-shrink-0 rounded-md border border-ui-border px-2 py-1 text-xs text-ui-text-muted hover:border-ui-border-strong hover:text-ui-text"
        >
          {rightIsOpen ? '← Cerrar' : 'Todo →'}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          title="Cerrar panel flotante"
          className="flex-shrink-0 flex h-6 w-6 items-center justify-center rounded-md text-ui-text-subtle hover:bg-ui-bg hover:text-ui-text"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-3" data-syncing={isSyncing}>
        <div className="space-y-3">
          {renderable.map((fieldName) => {
            const def = allFields[fieldName]!;
            return (
              <Field key={fieldName} label={def.label ?? fieldName}>
                <FieldControl
                  blockType={block.type}
                  fieldName={fieldName}
                  fieldDef={def}
                  value={block.props[fieldName]}
                  onChange={(next) => onPropsPatch({ [fieldName]: next })}
                  props={block.props}
                  locale={locale}
                />
              </Field>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
}

function EditorShell({
  page,
  sibling,
  pageId,
  navigate,
  initialData,
  initialSavedAt,
  onPageUpdate,
}: {
  page: Page;
  sibling: Page | null;
  pageId: string;
  navigate: NavigateFunction;
  initialData: Data;
  initialSavedAt: Date | null;
  onPageUpdate: (page: Page) => void;
}) {
  const { appState, dispatch } = usePuck();
  const appStateRef = useRef(appState);
  appStateRef.current = appState;

  const [leftW, setLeftW] = useState(() => loadWidth(LS_LEFT, PANEL_LEFT_DEFAULT));
  const [rightW, setRightW] = useState(() => loadWidth(LS_RIGHT, PANEL_RIGHT_DEFAULT));
  const [leftMode, setLeftMode] = useState<SidePanelMode>(preferredPanelMode);
  const [rightMode, setRightMode] = useState<SidePanelMode>('collapsed');
  const [leftResizing, setLeftResizing] = useState(false);
  const [rightResizing, setRightResizing] = useState(false);
  const [containerW, setContainerW] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [showLive, setShowLive] = useState(true);
  const [viewport, setViewport] = useState<Viewport>('desktop');
  const [saveState, setSaveState] = useState<SaveState>(initialSavedAt ? 'saved' : 'pristine');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(initialSavedAt);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [publishResult, setPublishResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [showSync, setShowSync] = useState(false);
  const [showSeo, setShowSeo] = useState(false);
  const [seoSaving, setSeoSaving] = useState(false);
  const [toolbarMode, setToolbarMode] = useState<'full' | 'compact' | 'hidden'>(defaultToolbarMode);
  const [seoForm, setSeoForm] = useState({
    metaTitle: page.metaTitle ?? '',
    metaDescription: page.metaDescription ?? '',
    ogImage: page.ogImage ?? '',
    noindex: page.noindex,
  });

  const [bufSrc, setBufSrc] = useState<[string | null, string | null]>([null, null]);
  const [activeBuf, setActiveBuf] = useState(0);
  const [previewReady, setPreviewReady] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [elementFocus, setElementFocus] = useState<{ fieldName: string; top: number; left: number } | null>(null);
  const [floatingDismissedAt, setFloatingDismissedAt] = useState(-99);
  const activeBufRef = useRef(0);
  const pendingBufRef = useRef(0);
  const iframeRefs = [useRef<HTMLIFrameElement>(null), useRef<HTMLIFrameElement>(null)];
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const liveBaseRef = useRef<string | null>(null);
  const previewOriginRef = useRef<string | null>(null);
  const tokenAtRef = useRef(0);
  const scrollYRef = useRef(0);
  const previewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedJsonRef = useRef(serializeData(initialData));
  const lastSeenJsonRef = useRef(lastSavedJsonRef.current);
  const historyRef = useRef<Data[]>([]);
  const futureRef = useRef<Data[]>([]);
  const applyingHistoryRef = useRef(false);
  const lastPersistPromiseRef = useRef<Promise<void> | null>(null);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryAttemptRef = useRef(0);
  const navigatorButtonRefs = useRef(new Map<number, HTMLElement | null>());

  useEffect(() => {
    const id = 'editor-field-sync-style';
    if (!document.getElementById(id)) {
      const el = document.createElement('style');
      el.id = id;
      el.textContent = [
        '@keyframes field-sync{0%,100%{opacity:1}50%{opacity:0.45}}',
        '@keyframes sync-bar{0%{transform:translateX(-100%)}100%{transform:translateX(100%)}}',
        '[data-syncing="true"] input,[data-syncing="true"] textarea,[data-syncing="true"] select{animation:field-sync 0.9s ease-in-out infinite;transition:none!important}',
        '.sync-progress-bar{position:absolute;top:0;left:0;right:0;height:2px;overflow:hidden;pointer-events:none}',
        '.sync-progress-bar::after{content:"";position:absolute;inset:0;background:linear-gradient(90deg,transparent,rgb(var(--ui-accent-ch)),transparent);animation:sync-bar 1.1s ease-in-out infinite}',
      ].join('');
      document.head.appendChild(el);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(LS_LEFT, String(leftW));
  }, [leftW]);
  useEffect(() => {
    localStorage.setItem(LS_RIGHT, String(rightW));
  }, [rightW]);
  useEffect(() => {
    function syncPanelsToViewport() {
      if (window.innerWidth < PANEL_WIDE_BREAKPOINT) {
        setLeftW((prev) => clampWidth(Math.min(prev, PANEL_LEFT_DEFAULT)));
        setRightW((prev) => clampWidth(Math.min(prev, PANEL_RIGHT_DEFAULT)));
      }
    }
    syncPanelsToViewport();
    window.addEventListener('resize', syncPanelsToViewport);
    return () => window.removeEventListener('resize', syncPanelsToViewport);
  }, []);

  useEffect(() => {
    const el = previewContainerRef.current;
    if (!el) return;
    setContainerW(el.offsetWidth);
    const ro = new ResizeObserver(([entry]) => setContainerW(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const selectedIndex = appState.ui.itemSelector?.index ?? -1;
  const selectedBlock = selectedIndex >= 0 ? appState.data.content[selectedIndex] : null;
  const localeStyle = LOCALE_STYLE[page.locale] ?? { bg: 'rgb(var(--ui-surface-2-ch))', color: 'rgb(var(--ui-text-muted-ch))', label: page.locale.toUpperCase() };
  const siblingLocaleStyle = sibling ? (LOCALE_STYLE[sibling.locale] ?? LOCALE_STYLE.en) : null;
  const currentRefWidth = VIEWPORT_CONFIG[viewport].refWidth;
  const currentScale = vpScale(currentRefWidth, containerW);
  const totalBlocks = appState.data.content.length;

  const navigatorBlocks = useMemo(() => {
    return appState.data.content.map((item, index) => ({
      index,
      type: item.type,
      label: blockLabel(item.type),
      summary: editorBlockMeta[item.type]?.summary(item.props as Record<string, unknown>) ?? blockLabel(item.type),
    }));
  }, [appState.data.content]);

  const hasPendingLocalChanges =
    saveState === 'saving' || saveState === 'error' || serializeData(appState.data) !== lastSavedJsonRef.current;

  function selectIndex(index: number) {
    dispatch({ type: 'setUi', ui: { itemSelector: { index, zone: ROOT_ZONE } } });
  }

  function commitData(nextData: Data, nextSelection?: number) {
    const normalized = ensureDataIds(nextData);
    dispatch({ type: 'setData', data: normalized });
    const index = typeof nextSelection === 'number' ? nextSelection : Math.min(normalized.content.length - 1, selectedIndex);
    if (index >= 0) {
      queueMicrotask(() => {
        dispatch({ type: 'setUi', ui: { itemSelector: { index, zone: ROOT_ZONE } } });
      });
    }
  }

  function insertBlock(type: string, index = appStateRef.current.data.content.length) {
    const current = cloneData(appStateRef.current.data);
    current.content.splice(index, 0, { type, props: { ...buildInitialProps(type), id: createId(type, index) } });
    commitData(current, index);
  }

  function removeBlock(index: number) {
    const current = cloneData(appStateRef.current.data);
    current.content.splice(index, 1);
    const nextSelection = current.content.length === 0 ? -1 : Math.max(0, Math.min(index, current.content.length - 1));
    commitData(current, nextSelection);
  }

  function duplicateBlock(index: number) {
    const current = cloneData(appStateRef.current.data);
    const source = current.content[index];
    if (!source) return;
    current.content.splice(index + 1, 0, {
      type: source.type,
      props: { ...source.props, id: createId(source.type, index + 1) },
    });
    commitData(current, index + 1);
  }

  function moveBlock(index: number, direction: -1 | 1) {
    const current = cloneData(appStateRef.current.data);
    const target = index + direction;
    if (target < 0 || target >= current.content.length) return;
    [current.content[index], current.content[target]] = [current.content[target], current.content[index]];
    commitData(current, target);
  }

  function patchSelectedBlock(patch: Record<string, unknown>) {
    if (selectedIndex < 0) return;
    const current = cloneData(appStateRef.current.data);
    const selected = current.content[selectedIndex];
    if (!selected) return;
    selected.props = { ...selected.props, ...patch };
    commitData(current, selectedIndex);
  }

  function patchBlockAt(index: number, patch: Record<string, unknown>) {
    const current = cloneData(appStateRef.current.data);
    const block = current.content[index];
    if (!block) return;
    block.props = { ...block.props, ...patch };
    commitData(current, index);
  }

  function undo() {
    const previous = historyRef.current.pop();
    if (!previous) return;
    futureRef.current.push(cloneData(appStateRef.current.data));
    applyingHistoryRef.current = true;
    commitData(previous, Math.min(selectedIndex, previous.content.length - 1));
  }

  function redo() {
    const next = futureRef.current.pop();
    if (!next) return;
    historyRef.current.push(cloneData(appStateRef.current.data));
    applyingHistoryRef.current = true;
    commitData(next, Math.min(selectedIndex, next.content.length - 1));
  }

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const isMod = e.metaKey || e.ctrlKey;
      if (isMod && e.key.toLowerCase() === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if (isMod && e.key.toLowerCase() === 'z' && e.shiftKey) {
        e.preventDefault();
        redo();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  });

  async function ensureToken() {
    if (liveBaseRef.current && Date.now() - tokenAtRef.current < 13 * 60 * 1000) return;
    const { url } = await api.preview.getUrl(pageId);
    liveBaseRef.current = url;
    tokenAtRef.current = Date.now();
    try {
      previewOriginRef.current = new URL(url).origin;
    } catch {
      previewOriginRef.current = null;
    }
  }

  function loadHiddenPreview() {
    if (!liveBaseRef.current) return;
    const next = (activeBufRef.current ^ 1) as 0 | 1;
    pendingBufRef.current = next;
    const src = `${liveBaseRef.current}&live=1&v=${Date.now()}#sy=${Math.round(scrollYRef.current)}`;
    setBufSrc((prev) => {
      const updated: [string | null, string | null] = [prev[0], prev[1]];
      updated[next] = src;
      return updated;
    });
  }

  function onBufLoad(i: 0 | 1) {
    if (i !== pendingBufRef.current) return;
    activeBufRef.current = i;
    setActiveBuf(i);
    setPreviewReady(true);
    setIsSyncing(false);
  }

  async function pushPreview(data: Data) {
    try {
      await ensureToken();
      await api.preview.saveLive(pageId, serializeData(data));
      loadHiddenPreview();
    } catch {
      // La vista en vivo es best-effort. El editor sigue utilizable si falla.
    }
  }

  function scheduleRetry() {
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    const attempt = retryAttemptRef.current;
    retryAttemptRef.current = attempt + 1;
    const delay = Math.min(2000 * 2 ** attempt, 30000); // 2s, 4s, 8s… tope 30s
    retryTimerRef.current = setTimeout(() => {
      retryTimerRef.current = null;
      void persistDraftNow(appStateRef.current.data);
    }, delay);
  }

  async function persistDraftNow(data: Data) {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    const contentJson = serializeData(data);
    const run = (async () => {
      setSaveState('saving');
      try {
        const draft = await api.pages.saveDraft(pageId, contentJson);
        lastSavedJsonRef.current = contentJson;
        retryAttemptRef.current = 0;
        setLastSavedAt(draft.updatedAt ? new Date(draft.updatedAt) : new Date());
        setSaveState('saved');
      } catch {
        setSaveState('error');
        scheduleRetry();
      }
    })();
    lastPersistPromiseRef.current = run;
    await run;
  }

  async function flushAutosave() {
    if (persistTimerRef.current || retryTimerRef.current) {
      if (persistTimerRef.current) {
        clearTimeout(persistTimerRef.current);
        persistTimerRef.current = null;
      }
      await persistDraftNow(appStateRef.current.data);
      return;
    }
    if (lastPersistPromiseRef.current) {
      await lastPersistPromiseRef.current;
    }
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await ensureToken();
        if (cancelled) return;
        await pushPreview(appStateRef.current.data);
        if (appStateRef.current.data.content.length > 0) selectIndex(0);
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pageId]);

  useEffect(() => {
    function onMsg(e: MessageEvent) {
      if (previewOriginRef.current && e.origin !== previewOriginRef.current) return;
      const data = e.data as {
        type?: string;
        index?: number;
        y?: number;
        action?: string;
        fieldName?: string;
        value?: string;
        rect?: { top: number; left: number; width: number; height: number; bottom: number };
      };
      if (!data || typeof data !== 'object') return;
      if (data.type === 'preview-scroll' && typeof data.y === 'number') {
        scrollYRef.current = data.y;
      } else if (data.type === 'puck-select' && typeof data.index === 'number') {
        setElementFocus(null);
        selectIndex(data.index);
      } else if (data.type === 'puck-element-focus' && typeof data.fieldName === 'string' && data.rect) {
        const iframeEl = iframeRefs[activeBufRef.current].current;
        const containerEl = previewContainerRef.current;
        if (iframeEl && containerEl) {
          const iframeR = iframeEl.getBoundingClientRect();
          const containerR = containerEl.getBoundingClientRect();
          const elRect = data.rect;
          const offsetTop = iframeR.top - containerR.top;
          const offsetLeft = iframeR.left - containerR.left;
          const POPOVER_H = 230;
          const belowTop = offsetTop + elRect.bottom + 10;
          const aboveTop = offsetTop + elRect.top - POPOVER_H - 10;
          const top = belowTop + POPOVER_H > containerEl.offsetHeight && aboveTop >= 0 ? aboveTop : belowTop;
          const left = Math.max(8, Math.min(offsetLeft + elRect.left, containerEl.offsetWidth - 280 - 8));
          setElementFocus({ fieldName: data.fieldName, top, left });
        }
      } else if (data.type === 'puck-toolbar-action' && typeof data.index === 'number') {
        const idx = data.index;
        if (data.action === 'move-up') moveBlock(idx, -1);
        else if (data.action === 'move-down') moveBlock(idx, 1);
        else if (data.action === 'duplicate') duplicateBlock(idx);
        else if (data.action === 'delete') removeBlock(idx);
      } else if (data.type === 'puck-field-edit' && typeof data.index === 'number' && typeof data.fieldName === 'string') {
        patchBlockAt(data.index, { [data.fieldName]: data.value ?? '' });
      }
    }
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  useEffect(() => {
    const idx = appState.ui.itemSelector?.index ?? -1;
    const block = idx >= 0 ? appState.data.content[idx] : null;
    const textFields: Record<string, string> = {};
    if (block) {
      const component = puckConfig.components[block.type] as { fields?: Record<string, PuckFieldDef> } | undefined;
      const blockFields = component?.fields ?? {};
      for (const [key, def] of Object.entries(blockFields)) {
        const isRenderedText =
          def?.type === 'text' || def?.type === 'textarea' || !def?.type || def?.isAiText === true;
        if (isRenderedText && typeof (block.props as Record<string, unknown>)[key] === 'string') {
          textFields[key] = (block.props as Record<string, unknown>)[key] as string;
        }
      }
    }
    setElementFocus(null);
    iframeRefs[activeBuf].current?.contentWindow?.postMessage(
      {
        type: 'puck-selected',
        index: idx,
        scroll: true,
        blockLabel: block ? blockLabel(block.type) : '',
        textFields,
        isLast: idx >= 0 && idx >= appState.data.content.length - 1,
      },
      previewOriginRef.current || '*',
    );
  }, [appState.ui.itemSelector, activeBuf]);

  useEffect(() => {
    if (selectedIndex < 0) return;
    const node = navigatorButtonRefs.current.get(selectedIndex);
    node?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selectedIndex]);

  useEffect(() => {
    const json = serializeData(appState.data);
    const previousJson = lastSeenJsonRef.current;
    if (json === previousJson) return;

    if (applyingHistoryRef.current) {
      applyingHistoryRef.current = false;
    } else {
      historyRef.current.push(cloneData(parseContentJson(previousJson)));
      if (historyRef.current.length > 40) historyRef.current.shift();
      futureRef.current = [];
    }
    lastSeenJsonRef.current = json;

    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    if (showLive) {
      previewTimerRef.current = setTimeout(() => {
        setIsSyncing(true);
        void pushPreview(appStateRef.current.data);
      }, 1000);
    }

    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    setSaveState('saving');
    persistTimerRef.current = setTimeout(() => {
      persistTimerRef.current = null;
      void persistDraftNow(appStateRef.current.data);
    }, 900);
  }, [appState.data, showLive]);

  useEffect(() => {
    function onKeyDown() {
      if (!previewTimerRef.current) return;
      clearTimeout(previewTimerRef.current);
      previewTimerRef.current = setTimeout(() => {
        setIsSyncing(true);
        void pushPreview(appStateRef.current.data);
      }, 1000);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    function beforeUnload(e: BeforeUnloadEvent) {
      if (!hasPendingLocalChanges) return;
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [hasPendingLocalChanges]);

  useEffect(() => {
    return () => {
      if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, []);


  async function handleSaveVersion() {
    await flushAutosave();
    const updated = await api.pages.saveContent(pageId, serializeData(appStateRef.current.data));
    onPageUpdate(updated);
    setLastSavedAt(updated.updatedAt ? new Date(updated.updatedAt) : new Date());
    setSaveState('saved');
  }

  async function handlePublish() {
    try {
      await flushAutosave();
      await api.publish.publishPage(pageId);
      await api.publish.trigger();
      setPublishResult({
        ok: true,
        message: 'Los cambios se publicaron correctamente. El sitio se estará actualizando en los próximos minutos.',
      });
    } catch (err) {
      setPublishResult({
        ok: false,
        message: `Ocurrió un problema al publicar: ${(err as Error).message}`,
      });
    }
  }

  async function handlePreview() {
    await flushAutosave();
    await pushPreview(appStateRef.current.data);
    const { url } = await api.preview.getUrl(pageId);
    setPreviewUrl(`${url}&live=1`);
  }

  async function handleSeoSave() {
    setSeoSaving(true);
    try {
      const updated = await api.pages.update(pageId, {
        metaTitle: seoForm.metaTitle || null,
        metaDescription: seoForm.metaDescription || null,
        ogImage: seoForm.ogImage || null,
        noindex: seoForm.noindex,
      });
      onPageUpdate(updated);
      setShowSeo(false);
    } finally {
      setSeoSaving(false);
    }
  }

  function doSync() {
    if (!sibling) return;
    commitData(parseContentJson(sibling.contentJson), 0);
    setShowSync(false);
  }

  function previewPaneStyle(index: 0 | 1): React.CSSProperties {
    return {
      position: 'absolute',
      inset: 0,
      display: 'flex',
      justifyContent: 'center',
      opacity: activeBuf === index ? 1 : 0,
      pointerEvents: activeBuf === index ? 'auto' : 'none',
      transition: 'opacity 120ms ease',
    };
  }

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'rgb(var(--ui-bg-ch))' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderBottom: '1px solid rgb(var(--ui-border-ch))', flexWrap: 'wrap' }}>
        <button onClick={() => navigate('/site?tab=paginas')} className="rounded-md border border-ui-border px-3 py-2 text-sm text-ui-text-muted hover:border-ui-border-strong hover:text-ui-text">
          ← Páginas
        </button>
        <span style={{ background: localeStyle.bg, color: localeStyle.color, padding: '4px 8px', borderRadius: 4, fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.08em' }}>
          {localeStyle.label}
        </span>
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-ui-text">{page.title}</div>
          <div className="text-xs text-ui-text-subtle">
            {saveState === 'saving'
              ? 'Guardando cambios…'
              : saveState === 'error'
                ? 'No se pudo guardar. Se intentará de nuevo.'
                : saveState === 'pristine'
                  ? 'Sin cambios todavía'
                  : lastSavedAt
                    ? `Todo guardado · ${fmtSavedAt(lastSavedAt)}`
                    : 'Todo guardado'}
          </div>
        </div>

        {sibling && siblingLocaleStyle && (
          <LanguagePopover
            currentLabel={localeStyle.label}
            siblingLabel={siblingLocaleStyle.label}
            onOpenSibling={() => navigate(`/editor/${sibling.id}`)}
            onCopyBlocks={() => setShowSync(true)}
          />
        )}

        {selectedBlock && (
          <div className="flex items-center gap-2 rounded-md border border-ui-border px-3 py-1.5">
            <span className="text-xs font-bold tracking-widest text-ui-accent">B{selectedIndex + 1}</span>
            <span className="text-sm text-ui-text">{blockLabel(selectedBlock.type)}</span>
          </div>
        )}

        <div className="ml-auto flex items-center gap-2">
          <button onClick={undo} disabled={historyRef.current.length === 0} className="rounded-md border border-ui-border p-2 text-ui-text-muted hover:border-ui-border-strong hover:text-ui-text disabled:opacity-40">
            <Undo2 size={16} />
          </button>
          <button onClick={redo} disabled={futureRef.current.length === 0} className="rounded-md border border-ui-border p-2 text-ui-text-muted hover:border-ui-border-strong hover:text-ui-text disabled:opacity-40">
            <Redo2 size={16} />
          </button>
          {showLive && (
            <div className="flex items-center rounded-md border border-ui-border bg-ui-surface-2 p-1">
              {([
                { vp: 'desktop' as const, Icon: Monitor },
                { vp: 'tablet'  as const, Icon: Tablet  },
                { vp: 'mobile'  as const, Icon: Smartphone },
              ]).map(({ vp, Icon }) => {
                const cfg = VIEWPORT_CONFIG[vp];
                const zoomPct = vpZoomPct(cfg.refWidth, containerW);
                const active = viewport === vp;
                const title = zoomPct < 100
                  ? `${cfg.label} · vista reducida al ${zoomPct}% (no cabe el ancho completo)`
                  : `${cfg.label} · ${cfg.refWidth}px`;
                return (
                  <button
                    key={vp}
                    onClick={() => setViewport(vp)}
                    title={title}
                    className={`inline-flex items-center gap-1 rounded px-2 py-1 ${active ? 'bg-ui-bg text-ui-text' : 'text-ui-text-subtle'}`}
                  >
                    <Icon size={15} />
                    {active && zoomPct < 100 && (
                      <span className="text-[10px] font-medium leading-none text-ui-text-muted">{zoomPct}%</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          <button onClick={() => setShowSeo(true)} className="rounded-md border border-ui-border px-3 py-2 text-sm text-ui-text-muted hover:border-ui-border-strong hover:text-ui-text">
            SEO
          </button>
          <button onClick={handlePreview} title="Vista previa" className="rounded-md border border-ui-border p-2 text-ui-text-muted hover:border-ui-border-strong hover:text-ui-text">
            <Eye size={16} />
          </button>
          <button onClick={handleSaveVersion} className="rounded-md border border-ui-border px-3 py-2 text-sm text-ui-text-muted hover:border-ui-border-strong hover:text-ui-text">
            <span className="inline-flex items-center gap-2"><Save size={16} /> Guardar</span>
          </button>
          <PublishButton
            label="Publicar"
            onPublish={handlePublish}
            className="rounded-md bg-ui-accent px-4 py-2 text-sm font-medium text-ui-text-inverse hover:bg-ui-accent-hover disabled:opacity-50 transition-colors"
          />
        </div>
      </div>

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {!fullscreen && (
          <>
            <motion.div
              animate={{ width: leftMode === 'open' ? leftW : PANEL_RAIL_WIDTH }}
              transition={leftResizing ? { duration: 0 } : { type: 'spring', stiffness: 300, damping: 30 }}
              style={{
                flexShrink: 0,
                overflow: 'hidden',
                borderRight: '1px solid rgb(var(--ui-border-ch))',
                background: 'rgb(var(--ui-surface-ch))',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <AnimatePresence initial={false} mode="wait">
                {leftMode === 'open' ? (
                  <motion.div
                    key="left-open"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.13 }}
                    style={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: leftW }}
                  >
                    <div className="space-y-3 border-b border-ui-border p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <div className="rounded-lg bg-ui-accent/15 p-2 text-ui-accent"><PanelLeft size={17} /></div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h2 className="text-sm font-semibold text-ui-text">Bloques</h2>
                              <span className="rounded-full bg-ui-bg px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-ui-text-subtle">
                                {totalBlocks}
                              </span>
                            </div>
                            <p className="text-xs text-ui-text-subtle">Organiza el contenido de la página.</p>
                          </div>
                        </div>
                        <PanelIconButton title="Contraer navegador" icon={<Minimize2 size={14} />} onClick={() => setLeftMode('collapsed')} />
                      </div>
                      <AddBlockMenu onAdd={(type) => insertBlock(type)} />
                    </div>
                    <div className="flex-1 overflow-y-auto p-3">
                      <div className="space-y-2">
                        {navigatorBlocks.map((block) => {
                          const meta = editorBlockMeta[block.type];
                          const Icon = meta?.icon ?? LayoutList;
                          const active = block.index === selectedIndex;
                          return (
                            <div
                              key={`${block.type}-${block.index}`}
                              ref={(node) => {
                                navigatorButtonRefs.current.set(block.index, node);
                              }}
                              role="button"
                              tabIndex={0}
                              aria-pressed={active}
                              onClick={() => selectIndex(block.index)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  selectIndex(block.index);
                                }
                              }}
                              className={`cursor-pointer rounded-xl border p-3 transition-colors ${active ? 'border-ui-accent bg-ui-accent/10' : 'border-ui-border bg-ui-bg hover:border-ui-border-strong'}`}
                            >
                              <div className="flex w-full items-start gap-3 text-left">
                                <div className={`rounded-lg p-2 ${active ? 'bg-ui-accent/20 text-ui-accent' : 'bg-ui-surface text-ui-text-subtle'}`}>
                                  <Icon size={16} />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="truncate text-sm font-medium text-ui-text">{block.label}</span>
                                    <span className="text-[11px] uppercase tracking-wide text-ui-text-subtle">#{block.index + 1}</span>
                                  </div>
                                  <p className="mt-1 line-clamp-2 text-xs text-ui-text-subtle">{block.summary}</p>
                                </div>
                              </div>
                              <div className="mt-3 flex items-center gap-2">
                                <button type="button" onClick={(e) => { e.stopPropagation(); moveBlock(block.index, -1); }} className="rounded-md border border-ui-border p-1.5 text-ui-text-subtle hover:text-ui-text"><ArrowUp size={13} /></button>
                                <button type="button" onClick={(e) => { e.stopPropagation(); moveBlock(block.index, 1); }} className="rounded-md border border-ui-border p-1.5 text-ui-text-subtle hover:text-ui-text"><ArrowDown size={13} /></button>
                                <button type="button" onClick={(e) => { e.stopPropagation(); duplicateBlock(block.index); }} className="rounded-md border border-ui-border p-1.5 text-ui-text-subtle hover:text-ui-text"><Copy size={13} /></button>
                                <button type="button" onClick={(e) => { e.stopPropagation(); insertBlock(block.type, block.index + 1); }} className="rounded-md border border-ui-border p-1.5 text-ui-text-subtle hover:text-ui-text"><Plus size={13} /></button>
                                <button type="button" onClick={(e) => { e.stopPropagation(); removeBlock(block.index); }} className="ml-auto rounded-md border border-ui-border p-1.5 text-red-400 hover:border-red-400/40"><Trash2 size={13} /></button>
                              </div>
                            </div>
                          );
                        })}
                        {navigatorBlocks.length === 0 && (
                          <div className="rounded-xl border border-dashed border-ui-border p-6 text-center text-sm text-ui-text-subtle">
                            Aún no hay bloques. Añade el primero para empezar.
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="left-collapsed"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.13 }}
                    style={{ height: '100%' }}
                  >
                    <CollapsedPanelRail
                      side="left"
                      title="Bloques"
                      meta={`${totalBlocks}`}
                      icon={<PanelLeft size={18} />}
                      onExpand={() => setLeftMode('open')}
                    >
                      <AddBlockMenu onAdd={(type) => insertBlock(type)} compact />
                    </CollapsedPanelRail>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
            {leftMode === 'open' && (
              <Divider
                onResize={(dx) => setLeftW((prev) => clampWidth(prev + dx))}
                onResizeStart={() => setLeftResizing(true)}
                onResizeEnd={() => setLeftResizing(false)}
              />
            )}
          </>
        )}

        <div ref={previewContainerRef} style={{ flex: 1, minWidth: 0, position: 'relative', background: 'rgb(var(--ui-bg-ch))' }}>
          <AnimatePresence>
            {showLive && selectedBlock && !elementFocus && floatingDismissedAt !== selectedIndex && (
              <FloatingInspector
                block={{ type: selectedBlock.type, props: selectedBlock.props as Record<string, unknown> }}
                selectedIndex={selectedIndex}
                locale={page.locale}
                onPropsPatch={patchSelectedBlock}
                rightIsOpen={rightMode === 'open'}
                onToggleRight={() => setRightMode((prev) => (prev === 'open' ? 'collapsed' : 'open'))}
                onDismiss={() => setFloatingDismissedAt(selectedIndex)}
                isSyncing={isSyncing}
              />
            )}
          </AnimatePresence>
          <AnimatePresence>
            {showLive && !previewReady && (
              <motion.div
                key="preview-loading"
                initial={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
                style={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 25,
                  background: 'rgb(var(--ui-bg-ch))',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 14,
                }}
              >
                <div
                  className="animate-spin"
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    border: '2.5px solid rgb(var(--ui-border-ch))',
                    borderTopColor: 'rgb(var(--ui-accent-ch))',
                  }}
                />
                <p style={{ fontSize: 13, color: 'rgb(var(--ui-text-subtle-ch))', margin: 0 }}>
                  Preparando vista en vivo…
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {elementFocus && selectedBlock && (() => {
              const component = puckConfig.components[selectedBlock.type] as { fields?: Record<string, PuckFieldDef> } | undefined;
              const allFields = component?.fields ?? {};
              if (!allFields[elementFocus.fieldName]) return null;
              return (
                <ElementPopover
                  key={`${selectedIndex}-${elementFocus.fieldName}`}
                  block={{ type: selectedBlock.type, props: selectedBlock.props as Record<string, unknown> }}
                  fieldName={elementFocus.fieldName}
                  top={elementFocus.top}
                  left={elementFocus.left}
                  onClose={() => setElementFocus(null)}
                  onPropsPatch={patchSelectedBlock}
                  locale={page.locale}
                  isSyncing={isSyncing}
                />
              );
            })()}
          </AnimatePresence>

          <AnimatePresence>
            {isSyncing && showLive && (
              <motion.div
                key="preview-syncing"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18, ease: 'easeOut' }}
                style={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgb(var(--ui-bg-ch) / 0.55)',
                  backdropFilter: 'blur(6px)',
                  WebkitBackdropFilter: 'blur(6px)',
                  pointerEvents: 'none',
                }}
              >
                <div
                  className="animate-spin"
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    border: '2.5px solid rgb(var(--ui-border-ch))',
                    borderTopColor: 'rgb(var(--ui-accent-ch))',
                  }}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {false && selectedBlock && (
            <>
              {toolbarMode === 'full' && (
                <div className="absolute left-1/2 top-4 z-30 flex max-w-[calc(100%-2rem)] -translate-x-1/2 items-center gap-2 rounded-[26px] border border-white/10 bg-[#171b2de6] px-3 py-2 shadow-[0_18px_50px_rgba(5,10,25,0.38)] backdrop-blur-xl">
                  <div className="flex h-10 min-w-0 items-center gap-2 rounded-full bg-[#111422] px-3">
                    <div className="flex h-6 min-w-6 items-center justify-center rounded-full bg-ui-accent/15 px-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-ui-accent">
                      B{selectedIndex + 1}
                    </div>
                    <div className="max-w-[150px] truncate text-sm font-semibold text-ui-text-inverse">
                      {blockLabel(selectedBlock!.type)}
                    </div>
                  </div>

                  <ToolbarMenu label="Agregar" icon={<Plus size={15} />}>
                    <ToolbarAction
                      label="Agregar bloque arriba"
                      hint="Crea otro bloque igual antes de este."
                      onClick={() => insertBlock(selectedBlock!.type, selectedIndex)}
                    />
                    <ToolbarAction
                      label="Agregar bloque abajo"
                      hint="Crea otro bloque igual después de este."
                      onClick={() => insertBlock(selectedBlock!.type, selectedIndex + 1)}
                    />
                  </ToolbarMenu>

                  <ToolbarMenu label="Mover" icon={<MoveVertical size={15} />}>
                    <ToolbarAction
                      label="Subir una posición"
                      hint="Mueve este bloque antes del bloque anterior."
                      onClick={() => moveBlock(selectedIndex, -1)}
                    />
                    <ToolbarAction
                      label="Bajar una posición"
                      hint="Mueve este bloque después del siguiente bloque."
                      onClick={() => moveBlock(selectedIndex, 1)}
                    />
                  </ToolbarMenu>

                  <ToolbarMenu label="Más" icon={<MoreHorizontal size={15} />}>
                    <ToolbarAction
                      label="Duplicar bloque"
                      hint="Crea una copia para editarla sin rehacer el contenido."
                      onClick={() => duplicateBlock(selectedIndex)}
                    />
                    <ToolbarAction
                      label="Eliminar bloque"
                      hint="Quita este bloque de la página."
                      tone="danger"
                      onClick={() => removeBlock(selectedIndex)}
                    />
                  </ToolbarMenu>

                  <button
                    type="button"
                    onClick={() => setFullscreen((prev) => !prev)}
                    className="inline-flex h-10 items-center gap-1.5 rounded-full border border-ui-border bg-ui-surface-2 px-3 text-sm text-ui-text-muted hover:border-ui-border-strong hover:text-ui-text"
                    title={fullscreen ? 'Mostrar paneles' : 'Enfocar vista'}
                  >
                    <Eye size={15} />
                    <span>{fullscreen ? 'Paneles' : 'Vista'}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setToolbarMode('compact')}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-ui-border bg-ui-surface-2 text-ui-text-muted hover:border-ui-border-strong hover:text-ui-text"
                    title="Hacer barra compacta"
                  >
                    <Minimize2 size={15} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setToolbarMode('hidden')}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-ui-border bg-ui-surface-2 text-ui-text-muted hover:border-ui-border-strong hover:text-ui-text"
                    title="Ocultar barra"
                  >
                    <EyeOff size={15} />
                  </button>
                </div>
              )}

              {toolbarMode === 'compact' && (
                <div className="absolute left-1/2 top-4 z-30 -translate-x-1/2">
                  <button
                    type="button"
                    onClick={() => setToolbarMode('full')}
                    className="flex items-center gap-3 rounded-full border border-ui-border bg-ui-surface/95 px-5 py-3 shadow-2xl backdrop-blur hover:border-ui-border-strong"
                  >
                    <div className="h-1.5 w-12 rounded-full bg-ui-text-subtle" />
                    <span className="max-w-[260px] truncate text-sm font-medium text-ui-text">
                      Bloque {selectedIndex + 1}: {blockLabel(selectedBlock!.type)}
                    </span>
                    <Maximize2 size={16} className="text-ui-text-muted" />
                  </button>
                  <div className="mt-2 flex justify-center gap-2">
                    <button
                      type="button"
                      onClick={() => setToolbarMode('hidden')}
                      className="rounded-full border border-ui-border bg-ui-surface/90 px-3 py-1.5 text-xs text-ui-text-muted hover:text-ui-text"
                    >
                      Ocultar barra
                    </button>
                  </div>
                </div>
              )}

              {toolbarMode === 'hidden' && (
                <div className="absolute left-1/2 top-4 z-30 -translate-x-1/2">
                  <button
                    type="button"
                    onClick={() => setToolbarMode('compact')}
                    className="flex items-center gap-3 rounded-full border border-ui-border bg-ui-surface/95 px-5 py-2.5 shadow-2xl backdrop-blur hover:border-ui-border-strong"
                  >
                    <span className="text-sm text-ui-text-muted">Abrir</span>
                    <Maximize2 size={15} className="text-ui-text-muted" />
                  </button>
                </div>
              )}
            </>
          )}

          {showLive ? (
            <>
              {([0, 1] as const).map((i) => (
                <div key={i} style={previewPaneStyle(i)}>
                  {bufSrc[i] && (
                    <motion.div
                      animate={{ width: vpDisplayWidth(currentRefWidth, containerW) }}
                      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                      style={{ height: '100%', overflow: 'hidden', flexShrink: 0 }}
                    >
                      <div
                        style={{
                          width: currentRefWidth,
                          height: currentScale < 1 ? `${100 / currentScale}%` : '100%',
                          transform: currentScale < 1 ? `scale(${currentScale})` : undefined,
                          transformOrigin: 'top left',
                        }}
                      >
                        <iframe
                          ref={iframeRefs[i]}
                          title={`Vista en vivo ${i}`}
                          src={bufSrc[i] as string}
                          onLoad={() => onBufLoad(i)}
                          style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
                        />
                      </div>
                    </motion.div>
                  )}
                </div>
              ))}
              {!bufSrc[0] && !bufSrc[1] && (
                <div className="absolute inset-0 flex items-center justify-center text-sm text-ui-text-subtle">
                  Cargando vista en vivo…
                </div>
              )}
            </>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-8 text-center">
              <div className="rounded-full bg-ui-surface p-3 text-ui-text-subtle"><EyeOff size={20} /></div>
              <div>
                <p className="text-sm font-medium text-ui-text">La vista en vivo está oculta</p>
                <p className="mt-1 text-sm text-ui-text-subtle">Puedes seguir editando desde la lista y el inspector, o volver a activarla para trabajar visualmente.</p>
              </div>
              <button onClick={() => setShowLive(true)} className="rounded-md bg-ui-accent px-4 py-2 text-sm font-medium text-ui-text-inverse">
                Mostrar vista en vivo
              </button>
            </div>
          )}
        </div>

        {!fullscreen && (
          <>
            {rightMode === 'open' && (
              <Divider
                onResize={(dx) => setRightW((prev) => clampWidth(prev - dx))}
                onResizeStart={() => setRightResizing(true)}
                onResizeEnd={() => setRightResizing(false)}
              />
            )}
            <motion.div
              animate={{ width: rightMode === 'open' ? rightW : PANEL_RAIL_WIDTH }}
              transition={rightResizing ? { duration: 0 } : { type: 'spring', stiffness: 300, damping: 30 }}
              style={{
                flexShrink: 0,
                overflow: 'hidden',
                borderLeft: '1px solid rgb(var(--ui-border-ch))',
                background: 'rgb(var(--ui-surface-ch))',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <AnimatePresence initial={false} mode="wait">
                {rightMode === 'open' ? (
                  <motion.div
                    key="right-open"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.13 }}
                    style={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: rightW }}
                  >
                    <div className="flex items-center justify-end border-b border-ui-border px-4 py-2">
                      <PanelIconButton title="Contraer inspector" icon={<Minimize2 size={14} />} onClick={() => setRightMode('collapsed')} />
                    </div>
                    <div className="min-h-0 flex-1">
                      <BlockInspector
                        block={selectedBlock ? { type: selectedBlock.type, props: selectedBlock.props as Record<string, unknown> } : null}
                        selectedIndex={selectedIndex}
                        locale={page.locale}
                        onPropsPatch={patchSelectedBlock}
                        isSyncing={isSyncing}
                      />
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="right-collapsed"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.13 }}
                    style={{ height: '100%' }}
                  >
                    <CollapsedPanelRail
                      side="right"
                      title="Inspector"
                      meta={selectedIndex >= 0 ? `B${selectedIndex + 1}` : '---'}
                      icon={<PanelRight size={18} />}
                      accent={
                        selectedBlock ? (
                          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-ui-accent/12 text-ui-accent">
                            {(() => {
                              const Icon = editorBlockMeta[selectedBlock.type]?.icon ?? PanelRight;
                              return <Icon size={17} />;
                            })()}
                          </div>
                        ) : undefined
                      }
                      onExpand={() => setRightMode('open')}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </>
        )}
      </div>

      {showSeo && (
        <Modal title="SEO / Metadatos" onClose={() => setShowSeo(false)}>
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-xs text-ui-text-muted">Meta título</label>
              <input
                value={seoForm.metaTitle}
                onChange={(e) => setSeoForm((form) => ({ ...form, metaTitle: e.target.value }))}
                placeholder="Título para buscadores (50-60 caracteres)"
                className="w-full rounded border border-ui-border bg-ui-bg px-3 py-2 text-sm text-ui-text focus:outline-none focus:border-ui-accent"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-ui-text-muted">Meta descripción</label>
              <textarea
                value={seoForm.metaDescription}
                onChange={(e) => setSeoForm((form) => ({ ...form, metaDescription: e.target.value }))}
                rows={3}
                placeholder="Descripción para buscadores (150-160 caracteres)"
                className="w-full resize-none rounded border border-ui-border bg-ui-bg px-3 py-2 text-sm text-ui-text focus:outline-none focus:border-ui-accent"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-ui-text-muted">Imagen OG</label>
              <MediaPicker
                value={seoForm.ogImage ? [seoForm.ogImage] : []}
                onChange={(urls) => setSeoForm((form) => ({ ...form, ogImage: urls[0] ?? '' }))}
                multiple={false}
                folder="pages"
              />
            </div>
            <Checkbox
              checked={seoForm.noindex}
              onChange={(e) => setSeoForm((form) => ({ ...form, noindex: e.target.checked }))}
              label="Ocultar de buscadores"
            />
            <div className="flex gap-3 pt-2">
              <button onClick={() => setShowSeo(false)} className="flex-1 rounded-md border border-ui-border px-4 py-2 text-sm text-ui-text-muted hover:text-ui-text">
                Cancelar
              </button>
              <button onClick={handleSeoSave} disabled={seoSaving} className="flex-1 rounded-md bg-ui-accent px-4 py-2 text-sm font-medium text-ui-text-inverse disabled:opacity-50">
                {seoSaving ? 'Guardando…' : 'Guardar SEO'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {previewUrl && (
        <Modal title="Vista previa" onClose={() => setPreviewUrl(null)}>
          <div className="h-[70vh] w-full overflow-hidden rounded bg-white">
            <iframe src={previewUrl} className="h-full w-full border-none" title="Vista previa" />
          </div>
          <div className="mt-4 flex items-center justify-between">
            <p className="text-xs italic text-ui-text-subtle">La vista usa tu borrador actual, incluso si todavía no publicas.</p>
            <a href={previewUrl} target="_blank" rel="noreferrer" className="text-xs text-ui-accent hover:underline">
              Abrir en nueva pestaña ↗
            </a>
          </div>
        </Modal>
      )}

      {publishResult && (
        <Modal title={publishResult.ok ? 'Página publicada' : 'No se pudo publicar'} onClose={() => setPublishResult(null)}>
          <p className={`text-sm ${publishResult.ok ? 'text-ui-text-muted' : 'text-ui-danger'}`}>{publishResult.message}</p>
          <button onClick={() => setPublishResult(null)} className="mt-4 w-full rounded-md border border-ui-border px-4 py-2 text-sm text-ui-text-muted hover:text-ui-text">
            Cerrar
          </button>
        </Modal>
      )}

      {showSync && sibling && (
        <Modal title={`Copiar bloques desde ${sibling.locale.toUpperCase()}`} onClose={() => setShowSync(false)}>
          <div className="space-y-4">
            <p className="text-sm text-ui-text-muted">
              Esto reemplazará los bloques actuales con los de la versión{' '}
              <span className="font-medium text-ui-text">{sibling.locale.toUpperCase()}</span>.
            </p>
            <p className="text-xs text-ui-text-subtle">
              Úsalo cuando quieras que ambos idiomas compartan el mismo orden y los mismos bloques.
            </p>
            <p className="text-xs text-amber-400">Los textos también se copiarán. Revísalos antes de publicar.</p>
            <div className="flex gap-3">
              <button onClick={doSync} className="flex-1 rounded-md bg-ui-accent px-4 py-2 text-sm font-medium text-ui-text-inverse">
                Sí, copiar bloques
              </button>
              <button onClick={() => setShowSync(false)} className="flex-1 rounded-md border border-ui-border px-4 py-2 text-sm text-ui-text-muted hover:text-ui-text">
                Cancelar
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function useMobileBlock(breakpoint = 768) {
  const [blocked, setBlocked] = useState(() => typeof window !== 'undefined' && window.innerWidth < breakpoint);
  useEffect(() => {
    function check() { setBlocked(window.innerWidth < breakpoint); }
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [breakpoint]);
  return blocked;
}

export default function EditorPage() {
  const { pageId } = useParams<{ pageId: string }>();
  const navigate = useNavigate();
  const [page, setPage] = useState<Page | null>(null);
  const [sibling, setSibling] = useState<Page | null>(null);
  const [initialData, setInitialData] = useState<Data | null>(null);
  const [initialSavedAt, setInitialSavedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const isMobileBlocked = useMobileBlock();

  useEffect(() => {
    if (!pageId) return;
    setSibling(null);
    setInitialData(null);
    setInitialSavedAt(null);
    setError(null);

    api.pages.get(pageId)
      .then(async (loadedPage) => {
        let draft: { contentJson: string; updatedAt: Date | null } = {
          contentJson: loadedPage.contentJson,
          updatedAt: loadedPage.updatedAt,
        };
        try {
          draft = await api.pages.getDraft(pageId);
        } catch (draftError) {
          if (!(draftError instanceof Error) || !/Not found/i.test(draftError.message)) {
            throw draftError;
          }
        }
        setPage(loadedPage);
        setEditorLocale(loadedPage.locale);
        setInitialData(parseContentJson(draft.contentJson));
        setInitialSavedAt(draft.updatedAt ? new Date(draft.updatedAt) : null);
        try {
          const loadedSibling = await api.pages.getSibling(loadedPage);
          setSibling(loadedSibling);
        } catch {
          setSibling(null);
        }
      })
      .catch((e: Error) => setError(e.message));
  }, [pageId]);

  if (isMobileBlocked) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-6 bg-ui-bg px-8 text-center">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 260, damping: 20 }}
          className="flex flex-col items-center gap-6"
        >
          <div className="flex items-center gap-3 rounded-2xl bg-ui-surface border border-ui-border p-4">
            <Monitor size={28} className="text-ui-accent" />
            <Tablet size={22} className="text-ui-text-muted" />
          </div>
          <div className="space-y-2">
            <h1 className="text-lg font-semibold text-ui-text">El editor necesita más espacio</h1>
            <p className="text-sm text-ui-text-muted max-w-xs">
              Para editar páginas abre el panel en tu computadora o en una tablet. En pantallas pequeñas el editor no cabe correctamente.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/site?tab=paginas')}
            className="rounded-md border border-ui-border px-4 py-2 text-sm text-ui-text-muted hover:border-ui-border-strong hover:text-ui-text"
          >
            ← Volver a páginas
          </button>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return <div className="flex h-screen items-center justify-center bg-ui-bg text-ui-danger">Error: {error}</div>;
  }
  if (!initialData || !page || !pageId) {
    return <div className="flex h-screen items-center justify-center bg-ui-bg text-ui-text-muted">Cargando editor…</div>;
  }

  return (
    <Puck key={pageId} config={puckConfig} data={initialData} iframe={{ enabled: false }}>
      <EditorShell
        page={page}
        sibling={sibling}
        pageId={pageId}
        navigate={navigate}
        initialData={initialData}
        initialSavedAt={initialSavedAt}
        onPageUpdate={setPage}
      />
    </Puck>
  );
}
